import React, {useCallback, useEffect, useState} from 'react';
import {useIntl} from 'react-intl';

import {Box, Button, BUTTON_VARIANT, Layout as AuroraLayout, SPACE, Text, useDevice, View} from '@lookiero/aurora';
import {Modality} from '@domain/model/Account/Subscription/Subscription';
import {Panel} from '@views/Account/Plan/_components/Panel';
import {useView} from '@views/Account/Plan/_hooks/useView';
import {FrequencySelector} from '@components/templates/FrequencySelector';

import {ModalRecoveryPlan} from '../ModalRecoveryPlan';
import {CTA_TEXT, IDS, TEXT} from './PanelChangePlan.definition';
import {style} from './PanelChangePlan.styles';
import {isCancellingPlan} from '../_utils/isCancellingPlan';
import {useUpdatePlan} from '../_hooks/useUpdatePlan';
import {useCurrentPlan} from '@views/Account/Plan/_hooks/useCurrentPlan';
import {useLoading} from '@views/Account/Plan/_hooks/useLoading';

interface PanelChangePlanProps {
  isVisible: boolean;
  onBack: () => void;
  onSuccess: () => Promise<void>;
  onClose: () => void;
}

export const PanelChangePlan: React.FC<PanelChangePlanProps> = ({
  isVisible: panelVisible,
  onBack,
  onSuccess,
  onClose,
}) => {
  const {screen} = useDevice();
  const {formatMessage} = useIntl();
  const {visible: modalVisible, open: openModal, close: closeModal} = useView();
  const {modality: currentModality, isOnDemand} = useCurrentPlan();
  const {updatePlan} = useUpdatePlan();
  const {loading, startLoading, stopLoading} = useLoading();

  const [selectedModality, setSelectedModality] = useState(currentModality);

  useEffect(() => {
    if (panelVisible) setSelectedModality(currentModality);
  }, [panelVisible, currentModality]);

  const updateModality = useCallback(
    async (newModality: Modality) => {
      try {
        startLoading();
        await updatePlan(newModality, async () => {
          await onSuccess();
          onClose();
          closeModal();
        });
      } finally {
        stopLoading();
      }
    },
    [updatePlan, onSuccess, onClose, closeModal, startLoading, stopLoading],
  );

  const handleSubmit = useCallback(async () => {
    if (isCancellingPlan(selectedModality)) {
      openModal();
      return;
    }

    await updateModality(selectedModality);
  }, [updateModality, selectedModality, openModal]);

  const styleBoxContainer = screen.L ? style.boxContainerL : style.boxContainerS;
  const isCtaDisabled = selectedModality === currentModality;

  return (
    <>
      <Panel id="panel-change-plan" visible={panelVisible} title={formatMessage(TEXT.PANEL_TITLE)} onBack={onBack}>
        <AuroraLayout style={style.layout}>
          <Box style={[style.boxContainer, styleBoxContainer]} size={{M: '2/3', L: '1/3'}}>
            <Text heading level={3} style={style.title}>
              {formatMessage(TEXT.PLANS_TITLE)}
            </Text>
            <FrequencySelector
              modality={selectedModality}
              onModalityChange={setSelectedModality}
              hideDescriptionOnBlur
            />
            <View marginTop={SPACE.SPACE_8}>
              <Text detail level={2} style={style.reassurance}>
                {formatMessage(TEXT.REASSURANCE1)}
              </Text>
              <Text detail level={2} style={style.reassurance}>
                {formatMessage(TEXT.REASSURANCE2)}
              </Text>
            </View>
            <Button
              onPress={handleSubmit}
              nativeID={IDS.SUBSCRIPTION_CTA}
              disabled={isCtaDisabled}
              marginTop={SPACE.SPACE_4}
            >
              {formatMessage(CTA_TEXT[selectedModality])}
            </Button>
            {!isOnDemand && (
              <>
                <Button onPress={openModal} variant={BUTTON_VARIANT.SECONDARY} marginTop={SPACE.SPACE_4}>
                  {formatMessage(TEXT.CTA_CANCEL)}
                </Button>
              </>
            )}
          </Box>
        </AuroraLayout>
      </Panel>
      <ModalRecoveryPlan
        isVisible={modalVisible}
        modality={currentModality}
        onClose={closeModal}
        onSubmit={updateModality}
        busy={loading}
      />
    </>
  );
};
